import React from 'react';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import { useSearchParams } from 'react-router-dom';
import { useShuls } from '../hooks/useShuls';
import ShulList from '../components/public/ShulList';
import ShulMapView from '../components/public/ShulMapView';
import { NearMeButton, NearMeNotice } from '../components/directory/NearMe';
import { useNearMe } from '../hooks/useNearMe';

export default function ShulsPage() {
  const { t } = useTranslation();
  usePageTitle(t('seo.shuls'), { description: t('seo.desc.shuls') });
  const { shuls, loading, error } = useShuls();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = searchParams.get('view') === 'map' ? 'map' : 'list';
  const focusShulId = searchParams.get('shul');
  const nearMe = useNearMe();
  const nearMeButton = <NearMeButton nearMe={nearMe} />;
  const nearMeNotice = <NearMeNotice nearMe={nearMe} />;

  const setView = (next: 'list' | 'map') => {
    const params = new URLSearchParams(searchParams);
    if (next === 'map') {
      params.set('view', 'map');
    } else {
      params.delete('view');
      params.delete('shul');
    }
    setSearchParams(params, { replace: true });
  };

  const viewToggle = (
    <div className="flex rounded-full border border-ink/15 p-0.5 shrink-0">
      <button
        type="button"
        onClick={() => setView('list')}
        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
          view === 'list' ? 'bg-ink text-paper' : 'text-ink'
        }`}
      >
        {t('home.listView')}
      </button>
      <button
        type="button"
        onClick={() => setView('map')}
        className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
          view === 'map' ? 'bg-ink text-paper' : 'text-ink'
        }`}
      >
        {t('home.mapView')}
      </button>
    </div>
  );

  if (view === 'map') {
    return (
      <>
        {/* Desktop/tablet page chrome — same padded column as before */}
        <div className="hidden sm:block max-w-5xl mx-auto px-4 pt-6">
          <div className="flex items-center justify-between gap-3 mb-6">
            <h1 className="text-lg font-semibold text-ink">{t('home.shulsHeading')}</h1>
            <div className="flex items-center gap-2">
              {nearMeButton}
              {viewToggle}
            </div>
          </div>
        </div>

        {/* Mobile: floating list/map toggle over the full-bleed map */}
        <div className="sm:hidden fixed z-[35] top-[calc(var(--nav-bottom)+0.75rem)] inset-x-4 flex justify-between gap-2 pointer-events-none">
          <div className="pointer-events-auto rounded-full shadow-[0_2px_6px_rgba(0,0,0,.04),0_4px_8px_rgba(0,0,0,.10)]">
            {nearMeButton}
          </div>
          <div className="pointer-events-auto bg-paper rounded-full shadow-[0_2px_6px_rgba(0,0,0,.04),0_4px_8px_rgba(0,0,0,.10)]">
            {viewToggle}
          </div>
        </div>

        {/* Single map instance: fixed full-bleed on mobile; padded column on sm+ */}
        <div className="sm:max-w-5xl sm:mx-auto sm:px-4 sm:pb-6">
          <ShulMapView
            shuls={shuls}
            loading={loading}
            error={error}
            focusShulId={focusShulId}
            origin={nearMe.location}
            notices={nearMeNotice}
          />
        </div>
      </>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-lg font-semibold text-ink">{t('home.shulsHeading')}</h1>
        {viewToggle}
      </div>
      <div className="space-y-2">
        <div className="flex">{nearMeButton}</div>
        {nearMeNotice}
      </div>
      <ShulList shuls={shuls} loading={loading} error={error} origin={nearMe.location} />
    </div>
  );
}
